import {Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ChangeDetectionStrategy} from "@angular/core";
import {DatePipe} from "@angular/common";
import {FormControl, FormGroup} from "@angular/forms";
import {MatAccordion} from "@angular/material/expansion";
import {Subscription} from "rxjs";
import {IrbOptionChains} from "./irb-option-chains.model";
import {IfilterOptionReq, IrbOptionChainsService} from "./irb-option-chains.service";
import {IrbAppEvents} from '@irb/app.events';
import {AppService} from '@irb/app.service';
import {IAlertDialogData} from "@irb/app.model";
import { DropdownFilterComponent } from "@irb/irb-common/components/dropdown-filter/dropdown-filter.component";
import { IDropdownItem } from "@irb/irb-common/models/dropdown.model";

@Component({ standalone: false,
  selector: 'irb-option-chains',
  templateUrl: './irb-option-chains.component.html',
  styleUrls: ['./irb-option-chains.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager
})
export class IrbOptionChainsComponent implements OnInit, OnDestroy {
  @ViewChild('IrbDropdownFilterOff') selectReference: DropdownFilterComponent;

  selectOptionsFunction: Function;
  formGroup: FormGroup;
  initialResponse: any;
  optionsChainTableData: {callExpDateMap?: any, putExpDateMap?: any, symbol?: string} = {};
  filterOptionSub: Subscription;
  expiringWorthLessValues: number[] = [0, 100];
  daysToExpiration: number[] = [0, 365];
  showAllExpirations = false;
  chainTypeOptions: IDropdownItem[] = [{name: 'Calls and Puts', value: 'ALL'}, {name: 'Calls', value: 'CALL'}, {name: 'Puts', value: 'PUT'}];
  chainTypeDisabled: boolean;
  selectedChainType: string;
  isDataAvailable = false;
  initialLoad = true;
  isTrayVisible = false;
  isNoResults = false;
  isSelectButtonDisable = true;
  map: any = new Map();
  currentTime: Date;
  showDetails: boolean;
  isDetailsLink : boolean;
  allExpandState = false;
  filterOptions: IfilterOptionReq;
  isFormSubmitted = false;
  strikePrice: string;
  openedPanels:any = [];
  arr = Array;
  probSliderNumber = 20;
  dteSliderNumber = 12;

  @ViewChild(MatAccordion) accordion: MatAccordion;
  @ViewChild('underlyingSymbolInput') public underlyingSymbolInput: ElementRef;
  @Input()
  optionChainData: IrbOptionChains;
  @Output()
  closeOptionChainDialog = new EventEmitter<any>();
  @ViewChild('overlayComponent') private overlay: any;

  constructor(
    private irbOptionChainsService: IrbOptionChainsService,
    public appService: AppService,
    private datePipe: DatePipe
  ) {}

  ngOnInit(): void {
    this.buildForm();
    this.handleSessionStorage();
  }
  
  setChainTypeOptions() {
    this.selectReference.set(this.chainTypeOptions);
  }
  
  selectOption(event: IDropdownItem) {
    this.formGroup.controls.chainType.setValue(event);
  }

  ngOnDestroy() {
    this.filterOptionSub && this.filterOptionSub.unsubscribe();
  }

  handleSessionStorage() {
    const showDetails = sessionStorage['OPTIONCHAIN_SHOWDETAILS'];
    const showAllExpirations = sessionStorage['OPTIONCHAIN_SHOWALLEXPIRATIONS'];
    if(!showDetails) {
      sessionStorage.setItem('OPTIONCHAIN_SHOWDETAILS', JSON.stringify(false));
    }
    if(!showAllExpirations) {
      sessionStorage.setItem('OPTIONCHAIN_SHOWALLEXPIRATIONS', JSON.stringify(false));
    }
    this.showDetails = JSON.parse(sessionStorage['OPTIONCHAIN_SHOWDETAILS']);
    this.showAllExpirations = JSON.parse(sessionStorage['OPTIONCHAIN_SHOWALLEXPIRATIONS']);
  }

  public handleShowDetails() {
    this.showDetails = !this.showDetails;
    sessionStorage.setItem('OPTIONCHAIN_SHOWDETAILS', JSON.stringify(this.showDetails));
  }

  handleShowAllExpiration() {
    sessionStorage.setItem('OPTIONCHAIN_SHOWALLEXPIRATIONS', JSON.stringify(this.showAllExpirations));
  }

  public getDTE(value: string): string {
    return value.split(':')[1];
  }

  public getDateValue(value: string): string {
    return this.datePipe.transform(value.split(':')[0], 'MMM dd yyyy');
  }

  public openAll() {
    this.overlay?.showWait();
    this.allExpandState = !this.allExpandState;
    this.accordion.openAll();
    this.overlay?.hideWait();
    this.isDetailsLink = true;
  }

  public closeAll() {
    this.overlay?.showWait();
    this.allExpandState = !this.allExpandState;
    this.accordion.closeAll();
    this.overlay?.hideWait();
    this.isDetailsLink = false;
  }

  openPanel (panelId: any) {
    this.openedPanels.push(panelId);
    this.allExpandState = true;
    this.isDetailsLink = true;
  }

  closePanel (panelId:any) {
    this.openedPanels =  this.openedPanels.filter( (item: any) => {
      return item !== panelId;
    });
    if(this.openedPanels.length > 0) {
      this.isDetailsLink = true;
      this.allExpandState = true;

    } else {
      this.isDetailsLink = false;
      this.allExpandState = false;
    }
  }

  public getProbWorth(value: number): number {
    const delta = Math.abs(value);
    return delta <= 1 ? Math.round((1 - this.getFloat(delta)) * 100) : 0;
  }

  public getFloat(delta: any): number {
    if (delta == 'NaN') { // Make it zero if it returns NaN for these options on the UI (delta, gamma, theta)
      return 0;
    }
    if (typeof delta !== "string") {
      delta = delta.toString();
    }
    return parseFloat(delta.match(/^-?\d+(?:\.\d{0,2})?/)[0]);
  }

  public returnZero() {
    return 0;
  }

  public handleOptionSubmit() {
    this.isFormSubmitted = true;
    const rawValue = this.formGroup.getRawValue();
    this.filterOptions = {
      ...rawValue,
      chainType: rawValue.chainType.value,
      strikePrice: this.strikePrice
    };
    this.callFilterOption();
  }

  public checkOptionChainResult(): void {
    const {callExpDateMap, putExpDateMap} = this.optionsChainTableData;
    if ((Object.entries(callExpDateMap).length === 0 && Object.entries(putExpDateMap).length === 0) ||
      (Object.entries(callExpDateMap).length === 0 && this.selectedChainType == 'Calls') ||
      (Object.entries(putExpDateMap).length === 0 && this.selectedChainType == 'Puts')) {
      this.isDataAvailable = false;
      if (this.map.size === 0) {
        this.isTrayVisible = false;
      }
      this.isNoResults = true;
      return;
    }
    this.isDataAvailable = true;
    this.isNoResults = false;
    if (this.optionChainData && this.optionChainData.underlyingSymbol && this.optionChainData.isUnderlyingSymbolDiable && !this.optionChainData.isTrayVisible) {
       this.isTrayVisible = false;
    } else {
      this.isTrayVisible = true;
    }
  }

  public addToTray(symbol: string, optionSymbol: string) {

    if (this.optionChainData && this.optionChainData.underlyingSymbol && this.optionChainData.isUnderlyingSymbolDiable && !this.optionChainData.canSelectMultiple) {
      this.map.set(symbol,[optionSymbol]);
      // console.log('addToTray', Array.from(this.map))
      this.irbOptionChainsService.addOptionChainSymbolSelections(Array.from(this.map));
      this.close();
      return;
    }

    if ( (this.optionChainData && this.optionChainData.canSelectMultiple) || !this.map.has(symbol) ) {
      const multiSelectedValues = this.map.get(symbol);
      if(multiSelectedValues) {
        const isValuePresent = multiSelectedValues.find((option: string) => option === optionSymbol);
        if(isValuePresent) {
          // console.log(optionSymbol, ' is already present');
          return;
        }
        multiSelectedValues.push(optionSymbol);
        this.map.set(symbol,multiSelectedValues);
      } else {
        this.map.set(symbol,[optionSymbol]);
      }
      this.isSelectButtonDisable = false;
    } else {
      this.appService.fireEvent(IrbAppEvents.SHOW_CONFIRMATION, {
        msg: symbol + " already exists as a selection, do you want to override it?",
        dType: "WARN",
        dProceedBtnLabel: "Yes",
        dCancelBtnLabel: "No",
        onProceed: (() => {
            this.map.set(symbol,[optionSymbol]);
            this.isSelectButtonDisable = false;
        }).bind(this)
      } as IAlertDialogData);
    }
  }

  public handleSliderProbWorthInputChange() {
    this.expiringWorthLessValues = [this.expiringWorthLessValues[0], this.expiringWorthLessValues[1]];
    this.handleSliderChange();
  }

  public handleSliderDTEInputChange() {
    this.daysToExpiration = [this.daysToExpiration[0], this.daysToExpiration[1]];
    this.handleSliderChange();
  }

  public handleSliderChange() {
    if(!this.initialResponse?.callExpDateMap || !this.initialResponse?.putExpDateMap) {
      return;
    }
    this.isDetailsLink = false;
    this.allExpandState = false;
    const callExpDateMap = this.initialResponse.callExpDateMap;
    const putExpDateMap = this.initialResponse.putExpDateMap;

    this.optionsChainTableData.callExpDateMap = this.getFilteredExpMap(callExpDateMap);
    this.optionsChainTableData.putExpDateMap = this.getFilteredExpMap(putExpDateMap);
    if (this.selectedChainType === 'Calls and Puts') {
      this.addPutKeyNotExistsInCalls();
    }
  }

  private getFilteredExpMap(expMap: any) {
    let filteredExpMap = {};
    for(const exp in expMap) {
      let filteredCalls: any = {};
      const currentDTE = parseInt(this.getDTE(exp));
      // console.log('daysToExpiration', this.daysToExpiration)
      if(this.showAllExpirations || (currentDTE >= this.daysToExpiration[0] && 
         currentDTE <= this.daysToExpiration[1])) {
        for(const range in expMap[exp]) {
            const currentRange = this.getProbWorth(expMap[exp][range][0].delta);
            if(currentRange >= this.expiringWorthLessValues[0] && 
                currentRange <= this.expiringWorthLessValues[1]) {
                  filteredCalls = {
                    ...filteredCalls,
                    [range]: expMap[exp][range]
                  };
            }  
        }
        if(Object.keys(filteredCalls).length) {
          filteredExpMap = { 
            ...filteredExpMap,
            [exp]: {...filteredCalls}
          }
        }
      }
    }
    // console.log('filteredExpMap', filteredExpMap);
    return filteredExpMap;
  }
  private addPutKeyNotExistsInCalls() {
    if (this.optionsChainTableData.putExpDateMap) {
      const putKeys = Object.keys(this.optionsChainTableData.putExpDateMap);
      if (putKeys && putKeys.length) {
        putKeys.forEach((key:any) => {
          if (!this.optionsChainTableData.callExpDateMap[key]) {
            this.optionsChainTableData.callExpDateMap[key] = {};
          }
        })
      }
    }

  }

  close(isCancel?:boolean) {
    this.map.clear();
    this.closeOptionChainDialog.emit(isCancel);
  }

  refresh() {
    this.formGroup.controls['underlyingSymbol'].setValue(this.filterOptions.underlyingSymbol);
    this.formGroup.controls['chainType'].setValue(this.formGroup.getRawValue().chainType);
    this.strikePrice = this.filterOptions.strikePrice;
    this.callFilterOption();
  }

  selectOptions() {
    this.irbOptionChainsService.addOptionChainSymbolSelections(Array.from(this.map));
    this.close();
  }

  deleteOption(deletedSymbolObj:any) {
    if (deletedSymbolObj.symbol === 'all') {
      this.appService.fireEvent(IrbAppEvents.SHOW_CONFIRMATION, {
        msg: "Are you sure you want to remove all selections?",
        dType: "WARN",
        dProceedBtnLabel: "Yes",
        dCancelBtnLabel: "No",
        onProceed: (() => {
          this.map.clear();
          if (this.optionChainData?.isChainTypeDisable && !this.optionChainData?.isUnderlyingSymbolDiable) {
            this.isSelectButtonDisable = false;
          }
          if (this.optionChainData?.canSelectMultiple && this.map.size === 0) {
            this.isSelectButtonDisable = true;
          }
        }).bind(this)
      } as IAlertDialogData);
    } else {
      const optionData = this.map.get(deletedSymbolObj.symbol).filter((item: any) => {
        return item !== deletedSymbolObj.symbolData;
      });
      if(optionData && optionData.length > 0) {
        this.map.set(deletedSymbolObj.symbol, optionData);
      } else  {
        this.map.delete(deletedSymbolObj.symbol);
      }
      if (this.optionChainData?.isChainTypeDisable && !this.optionChainData?.isUnderlyingSymbolDiable) {
        this.isSelectButtonDisable = false;
      }
      if (this.optionChainData?.canSelectMultiple && this.map.size === 0) {
        this.isSelectButtonDisable = true;
      }
    }
  }

  private buildForm() {
    const underlyingSymbolValue = this.optionChainData?.underlyingSymbol || null;
    const chainTypeValue = this.optionChainData?.chainType || 'ALL';
    const selectedChainType = this.chainTypeOptions.find((chainType) => chainType.value === chainTypeValue);
    this.formGroup = new FormGroup({
      underlyingSymbol: new FormControl({
        value: underlyingSymbolValue,
        disabled: this.optionChainData?.isUnderlyingSymbolDiable
      }),
      chainType: new FormControl({value: selectedChainType, disabled: this.optionChainData?.isChainTypeDisable}),
    });
    
    if (this.optionChainData?.isChainTypeDisable) {
      this.chainTypeDisabled = true;
    }
    
    if (this.optionChainData) {
      this.map = new Map(this.optionChainData.selectedOptions?.map(item => [item.symbol, [item.optionSymbol]]));
     if (this.optionChainData.underlyingSymbol) {
        this.handleOptionSubmit();
      }
      if(!this.optionChainData.underlyingSymbol && this.optionChainData.selectedOptions && this.optionChainData.selectedOptions.length > 0) {
        this.initialLoad = false;
        this.isTrayVisible = true;
        this.isSelectButtonDisable = true;
      }
    }
    setTimeout(() => {
      this.underlyingSymbolInput.nativeElement.focus();
    });
  }

  private callFilterOption(): void {
    this.overlay?.showWait();
    this.openedPanels = [];
    this.isDetailsLink = false;
    this.allExpandState = false;
    this.filterOptionSub = this.irbOptionChainsService.getFilterOptions(this.filterOptions).subscribe((response) => {
      this.initialLoad = false;
      //Storing initial Response for Filtering purpose
      this.initialResponse = JSON.parse(JSON.stringify(response.responseData));
      this.optionsChainTableData = response.responseData;
      this.selectedChainType = this.formGroup.getRawValue().chainType.name;
      this.currentTime = new Date();
      this.checkOptionChainResult();
      this.handleSliderChange();
      this.overlay?.hideWait();
    })
  }
}

